import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import ParallaxCard from "@/components/ParallaxCard";
import SEO, {
  breadcrumbSchema,
  serviceSchema,
  faqSchema,
} from "@/components/SEO";

const CONTENT = {
  consulting: {
    n: "I",
    eyebrow: "Advisory",
    caps: "Strategy that becomes reality.",
    script: "Consulting",
    scriptFirst: true,
    lede:
      "We help luxury brands build the culture, capabilities and human intelligence required to move from excellent to exceptional — and to become the next one.",
    manifestoTitle: {
      caps: "EXCELLENCE IS NO LONGER",
      script: "enough.",
    },
    manifesto: [
      "Luxury is evolving. Clients are changing, expectations are rising, and excellence alone is no longer enough to lead. There is already a great deal of excellence in luxury: beautiful products, refined service, trained teams, and strong standards. But exceptional is different. At Next in Luxury, we believe exceptional luxury is the rare alignment of soul and system. Soul is the emotional, cultural, and human truth of a brand — the meaning clients feel, teams believe in, and communities remember.",
      "System is what allows that meaning to become lived, operational, consistent, and scalable across every touchpoint, behavior and experience. Exceptional luxury is not the next level of performance. It is the next level of transformation. It is what happens when a brand moves beyond looking excellent and starts becoming exceptional from the inside out — when standards become instinct, service feels intentional, and teams embody the meaning behind every gesture.",
    ],
    hero: "https://images.squarespace-cdn.com/content/v1/68f648f746888b590facb861/a273806b-396c-4ac7-b21b-ba02b0bf4632/Consulting+next+in+luxury?format=2500w",
    phases: [
      {
        n: "I",
        eyebrow: "Phase I — We Analyse",
        caps: "DISCOVERY",
        script: "& Alignment",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/uzb6im8m_Screenshot%202026-07-19%20at%2012.13.13%E2%80%AFpm.png",
        layout: "spread",
        dek: "We begin with deep observation and strategic alignment — analysing brand perception, customer experience, operational standards, leadership, communication and culture to uncover both visible gaps and invisible patterns shaping the organisation.",
        items: [
          "Brand strategy & current brand performance",
          "Mystery shopping — experience & brand standards",
          "Customer journey diagnostics (“as is”)",
          "360° assessment: strategy, culture, experience, operations",
        ],
      },
      {
        n: "II",
        eyebrow: "Phase II — We Design & Implement",
        caps: "LUXURY",
        script: "Experience Design",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/wfx9dld6_Screenshot%202026-07-19%20at%202.10.55%E2%80%AFpm.png",
        layout: "spread",
        dek: "We shape emotionally resonant and operationally coherent systems aligned with the identity of the brand — transforming strategy into experiences, rituals, standards, behaviours and service cultures people can truly feel.",
        items: [
          "Brand strategy & positioning",
          "Brand concept design & storytelling",
          "Luxury journey map — sensorial, emotional, artification",
          "Experience calibration & measurement",
          "Content & communication strategy",
          "Luxury service culture",
          "Employee experience design",
          "Brand guidelines & operational standards",
        ],
      },
      {
        n: "III",
        eyebrow: "Phase III — We Embed",
        caps: "TRAINING",
        script: "& Operational Mastery",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/fuc9alfb_Screenshot%202026-07-21%20at%2012.19.53%E2%80%AFpm.png",
        layout: "spread",
        focus: "center",
        dek: "Transformation only becomes meaningful when it is fully embedded into the daily reality of the organisation. We work alongside leadership and operational teams to integrate systems, standards, behaviours, rituals and cultural alignment — ensuring long-term consistency, execution and transformation.",
        items: [
          "Tailored trainings",
          "Workshops",
          "Masterclasses",
          "Creative Lab",
          "Executive & operational leadership coaching",
        ],
      },
    ],
    expertise: [
      {
        caps: "STRATEGY",
        items: [
          "Brand strategy & positioning",
          "Strategic plan & roadmap",
          "Brand architecture & proposition",
          "Brand portfolio optimisation",
          "Innovation roadmap",
          "Customer experience strategy",
          "Customer retention (loyalty & clienteling)",
          "Concept design & development",
          "New market entry strategy",
          "Brand narrative & creative concept",
          "Tailored storytelling",
          "Brand collaborations",
          "Finance, pricing, revenue",
        ],
      },
      {
        caps: "COMMUNICATION",
        items: [
          "Content strategy & content marketing framework",
          "Storytelling & narrative strategy",
          "Content marketing implementation with lead gen",
          "Content creation & marketing creative oversight",
          "Content calendar & campaign roadmap",
          "Holistic content strategy & content ops model",
          "Editorial & visual style guides",
          "Social media & video strategy",
          "Curated events strategy aligned to content pillars",
          "New website UX, design & messaging",
          "Digital strategy, front & backend",
          "Website creation",
          "Marketing & PR",
        ],
      },
      {
        caps: "EXPERIENCE DESIGN",
        items: [
          "Customer journey mapping",
          "Service ceremony",
          "Service rituals & artification",
          "Brand toolkit / guidelines",
          "Brand experience guidelines",
          "Service guidelines",
          "Multisensory design",
          "Digital & physical experience alignment",
          "Employee experience journey",
          "Event journey & brand activations",
          "Design thinking",
          "Experiential storytelling",
        ],
      },
      {
        caps: "OPERATIONAL MASTERY",
        items: [
          "Experiential & emotional selling techniques",
          "Training manuals",
          "Standard operating procedures",
          "Omnichannel strategy",
          "Events & activations",
          "Experience metrics",
          "Strategy & design operationalisation",
          "From storytelling to storydoing",
          "Etiquette & protocol",
          "Communication & body language",
          "Personal presentation",
          "Material & asset stewarding",
          "Architectural brief with experiential flow",
        ],
      },
      {
        caps: "LEADERSHIP & CULTURE",
        items: [
          "Cultural transformation",
          "Luxury & service culture",
          "Customer-centric culture",
          "Leadership workshops",
          "Leading high-performing teams",
          "Mindset & wellbeing",
          "Emotional intelligence",
          "Public speaking for leadership & guest-facing teams",
          "Executive & leadership coaching",
          "From storytelling to storybeing",
        ],
      },
    ],
    roadmap: [
      { caps: "BESPOKE SCOPE", script: "& Definition", dek: "Every engagement begins with a blank page. No assumptions, no templates. We define the scope together." },
      { caps: "DISCOVERY", script: "& Alignment", dek: "An honest, critical, 360° view — mystery visits, team interviews, audits. What is said, unsaid — and what is felt." },
      { caps: "DESIGN", script: "Co-creation", dek: "Workshops, immersive sessions, scenario building. Emotionally resonant and operationally viable." },
      { caps: "IMPLEMENTATION", script: "& Integration", dek: "Side-by-side with your teams — embedding, training, empowering. Capability that remains long after we're gone." },
      { caps: "MEASUREMENT", script: "& Calibration", dek: "We co-establish metrics that measure both impact and integrity. True luxury is never static — neither are we." },
    ],
    next: { to: "/services/training", label: "TRAINING", script: "Savoir-faire that outlasts." },
  },
  training: {
    n: "II",
    eyebrow: "II. Transmission",
    caps: "Savoir-faire that outlasts.",
    script: "Training",
    lede:
      "We design transformative training journeys that combine cultural depth, operational mastery, emotional intelligence and experiential thinking — helping luxury professionals, leadership teams and organisations embody your brand beyond standards alone.",
    manifestoTitle: {
      caps: "THE ART OF\nBECOMING",
      script: "Exceptional.",
    },
    manifesto: [
      "Luxury cannot be transmitted through standards alone. Exceptional luxury happens when people understand the meaning behind every gesture, when service feels intentional rather than scripted, and when culture becomes embodied across every interaction.",
      "Powered by Expert Minds, our programs transmit the codes, behaviors, rituals, and strategic intelligence behind exceptional luxury. Because learning should not simply transfer knowledge. It should shape perspective, behavior, leadership, judgment and the ability to transform experiences from the inside out. Excellent professionals perform. Exceptional professionals transform the people, cultures, and experiences around them.",
    ],
    hero: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/jt9ogb4s_Saint%20James%20Paris%20-%20Bar%20Bibliothe%CC%80que%201.webp",
    tracks: [
      {
        n: "I",
        eyebrow: "For Executives, Senior Managers & Operational Leaders",
        caps: "LEADERSHIP",
        script: "From vision into strategy.",
        dek: "For executives, senior managers and operational leaders shaping the future of luxury organisations.",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/l9f2t73k_Screenshot%202026-07-21%20at%205.55.15%E2%80%AFpm.png",
        layout: "spread",
        focus: "center",
        items: [
          "Leading luxury teams",
          "Leadership assessment",
          "Luxury & service culture",
          "Operational excellence",
          "Luxury experience metrics & ROI",
        ],
      },
      {
        n: "II",
        eyebrow: "For Operational Leaders & Guest-Facing Teams",
        caps: "OPERATIONAL",
        script: "From strategy to mastery.",
        dek: "Building the daily disciplines — service standards, rituals and codes — that make excellence inevitable at every touchpoint.",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/3kwfyu85_Screenshot%202026-07-23%20at%201.06.20%E2%80%AFam.png",
        layout: "spread",
        focus: "center",
        items: [
          "Intuitive service",
          "Luxury service & etiquette",
          "Luxury communication",
          "Complaint handling",
          "Storytelling",
          "Butler service",
          "Clienteling",
        ],
      },
      {
        n: "III",
        eyebrow: "Bespoke Workshops",
        caps: "CREATIVE",
        script: "Excellence Lab.",
        dek: "Bespoke workshops to shape rituals, narratives and codes to become exceptional.",
        image: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/0yol4ix3_Screenshot%202026-07-23%20at%201.15.41%E2%80%AFam.png",
        layout: "spread",
        focus: "center",
        items: [
          "Guest / client journey experience design",
          "Employee experience design",
          "Hospitality & service / selling ceremonies",
          "Intuitive & anticipatory service",
          "Innovation & the future of luxury",
        ],
      },
    ],
    next: { to: "/services/consulting", label: "CONSULTING", script: "Strategy that becomes reality." },
  },
};

export default function ServicesDetail() {
  const { service } = useParams();
  const data = CONTENT[service] || CONTENT.consulting;
  const [expertiseHover, setExpertiseHover] = useState(null);
  const toRoman = (n) => ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"][n];

  const seoConfig = {
    consulting: {
      title: "Luxury Consulting — Strategy That Becomes Reality",
      description:
        "Luxury consulting by Next in Luxury: strategic advisory, brand strategy, customer experience and operational transformation for exceptional luxury brands in hospitality, retail, culture and tourism.",
      category: "Luxury Consulting & Strategic Advisory",
      faq: [
        {
          q: "What is luxury consulting at Next in Luxury?",
          a: "Luxury consulting at Next in Luxury is a strategic advisory practice that helps brands align soul and system — defining brand strategy, customer experience, operational standards and cultural direction to move from excellent to exceptional.",
        },
        {
          q: "Which sectors does Next in Luxury Consulting serve?",
          a: "The Consulting practice serves luxury hospitality, luxury retail, private clienteling, tourism, culture, gastronomy and exceptional experiences worldwide.",
        },
        {
          q: "How does the Consulting engagement work?",
          a: "Every engagement follows a structured methodology — Discovery & Alignment, Diagnosis, Vision & Strategy, Design & Implementation, Activation and Evolution — tailored to the brand's ambition and maturity.",
        },
      ],
    },
    training: {
      title: "Luxury Training — Savoir-Faire That Outlasts",
      description:
        "Luxury training by Next in Luxury: executive programmes, service excellence, savoir-faire transmission, leadership and team development for luxury hospitality, retail and cultural institutions.",
      category: "Luxury Training & Executive Programmes",
      faq: [
        {
          q: "What is luxury training at Next in Luxury?",
          a: "Luxury training at Next in Luxury is the transmission practice — bespoke programmes that build the culture, capabilities and human intelligence of leaders, teams and institutions in the luxury industry.",
        },
        {
          q: "Who are the Training programmes for?",
          a: "The Training programmes are designed for executives, general managers, service teams, boutique staff, front-of-house professionals and future leaders across luxury hospitality, retail and culture.",
        },
        {
          q: "Are the Training programmes customised?",
          a: "Yes. Every programme is co-designed with the client to reflect the brand's DNA, standards, rituals and ambitions — combining classroom, on-the-floor coaching and cultural immersion.",
        },
      ],
    },
  };

  const cfg = seoConfig[service] || seoConfig.consulting;
  const activeService = service === "training" ? "training" : "consulting";

  return (
    <div data-testid={`page-service-${service}`} className="pt-32">
      <SEO
        title={cfg.title}
        description={cfg.description}
        path={`/services/${activeService}`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            {
              name: activeService === "training" ? "Training" : "Consulting",
              path: `/services/${activeService}`,
            },
          ]),
          serviceSchema({
            name: cfg.title.split(" — ")[0],
            description: cfg.description,
            path: `/services/${activeService}`,
            category: cfg.category,
          }),
          faqSchema(cfg.faq),
        ]}
      />
      {/* ═══ HEADER ═══ */}
      {(service === "consulting" || service === "training") ? (
        (() => {
          const heroImages = {
            consulting:
              "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/q04s9b02_Screenshot%202026-07-16%20at%2010.02.59%E2%80%AFpm.png",
            training:
              "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/4sjap0ud_Screenshot%202026-07-16%20at%2012.32.37%E2%80%AFam.png",
          };
          return (
        <section
          className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-8 pb-24"
          data-testid={`${service}-hero`}
        >
          <div className="mb-10">
            <p className="font-eyebrow opacity-60">
              <Link
                to="/services"
                className="editorial-link"
                data-testid="service-back-link"
              >
                ← Services
              </Link>
            </p>
          </div>

          <div className="grid grid-cols-12 gap-8 md:gap-16 items-stretch">
            {/* LEFT — copy */}
            <div className="col-span-12 md:col-span-7 flex flex-col justify-center">
              <p
                className="font-eyebrow hero-word"
                style={{ color: "#8C6B3F", animationDelay: "0ms" }}
              >
                {data.eyebrow}
              </p>

              <p
                className="font-script hero-word mt-6"
                style={{
                  textTransform: "none",
                  letterSpacing: 0,
                  color: "#8C6B3F",
                  fontSize: "clamp(3rem, 6.5vw, 6rem)",
                  lineHeight: 1,
                  animationDelay: "150ms",
                }}
              >
                {data.script}
              </p>

              <h1
                className="title-caps mt-4"
                style={{
                  fontSize: "clamp(2.4rem, 6.8vw, 6.75rem)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.008em",
                }}
                data-testid={`service-title-${service}`}
              >
                {data.caps.split(" ").map((w, i, arr) => (
                  <span
                    key={i}
                    className="hero-word"
                    style={{
                      marginRight: i === arr.length - 1 ? 0 : "0.28em",
                      animationDelay: `${380 + i * 130}ms`,
                    }}
                  >
                    {w}
                  </span>
                ))}
              </h1>

              <p
                className="font-body opacity-85 mt-12 max-w-2xl text-lg leading-relaxed hero-word"
                style={{
                  animationDelay: `${
                    380 + data.caps.split(" ").length * 130 + 140
                  }ms`,
                }}
              >
                {data.lede}
              </p>
            </div>

            {/* RIGHT — vertical portrait image */}
            <div className="col-span-12 md:col-span-5">
              <div
                className="w-full hero-word"
                style={{
                  animationDelay: "80ms",
                  animationDuration: "1200ms",
                }}
              >
                <div className="p-2 md:p-3" style={{ border: "1px solid #B8B2A6" }}>
                  <ParallaxCard
                    src={heroImages[service]}
                    alt={`${data.caps} — architectural context`}
                    aspect="aspect-[3/4]"
                    data-testid={`${service}-hero-image`}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
          );
        })()
      ) : (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <p className="font-eyebrow opacity-60">
              <Link to="/services" className="editorial-link" data-testid="service-back-link">
                ← Services
              </Link>
            </p>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Reveal>
              <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                {data.eyebrow}
              </p>
              <h1 className="title-caps leading-[0.9] mt-8" style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }} data-testid={`service-title-${service}`}>
                {data.scriptFirst ? (
                  <>
                    <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.15em", color: "#8C6B3F", display: "inline-block" }}>
                      {data.script}
                    </span>
                    <br />
                    {data.caps}
                  </>
                ) : (
                  <>
                    {data.caps}
                    <br />
                    <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.15em", color: "#8C6B3F" }}>
                      {data.script}
                    </span>
                  </>
                )}
              </h1>
              <p className="font-body opacity-85 mt-12 max-w-2xl text-lg leading-relaxed">{data.lede}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Hero image — cinematic slow Ken-Burns (not shown for consulting) */}
      {service !== "consulting" && (
        <div className="w-full h-[75vh] my-16 overflow-hidden bg-[#0A0A08] relative">
          <img
            src={data.hero}
            alt={data.caps}
            className="w-full h-full object-cover"
            style={{
              filter: "grayscale(1) contrast(1.2) brightness(0.85) saturate(0)",
              animation: "slowKenBurns 22s ease-in-out infinite alternate",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(20,20,15,0) 60%, rgba(20,20,15,0.35) 100%)",
              pointerEvents: "none",
            }}
          />
        </div>
      )}

      {/* Manifesto — formatted to match homepage Founders' Note */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <Reveal>
            <div className="hairline mb-6" />
            <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
              The Belief
            </p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal delay={200}>
            {data.manifestoTitle && (
              <h2
                className="title-caps leading-[0.95]"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 5rem)",
                  whiteSpace: "pre-line",
                }}
              >
                {data.manifestoTitle.caps}
                {data.manifestoTitle.caps.includes("\n") && <br />}{" "}
                <span
                  className="font-script"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    fontSize: "1.25em",
                    color: "#8C6B3F",
                  }}
                >
                  {data.manifestoTitle.script}
                </span>
              </h2>
            )}
            {(Array.isArray(data.manifesto) ? data.manifesto : [data.manifesto]).map((para, i) => (
              <p
                key={i}
                className="font-body mt-12 max-w-2xl opacity-85 text-lg leading-relaxed"
              >
                {para}
              </p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* THE SHIFT — Consulting-only comparative section */}
      {service === "consulting" && (
        <section
          className="relative"
          style={{ background: "#F4EFE7" }}
          data-testid="consulting-shift"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48">
            {/* Header — matched to Founders' Note formatting */}
            <div className="grid grid-cols-12 gap-8 mb-24 md:mb-32">
              <div className="col-span-12 md:col-span-4">
                <Reveal>
                  <div className="hairline mb-6" />
                  <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                    The Shift
                  </p>
                </Reveal>
              </div>
              <div className="col-span-12 md:col-span-8">
                <Reveal delay={200}>
                  <h2
                    className="title-caps leading-[0.98]"
                    style={{
                      fontSize: "clamp(2.25rem, 5vw, 5rem)",
                      color: "#14140F",
                      textWrap: "balance",
                    }}
                  >
                    EXCELLENT{" "}
                    <span
                      className="font-script"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: "1.25em",
                        color: "#8C6B3F",
                      }}
                    >
                      is not
                    </span>{" "}
                    EXCEPTIONAL.
                  </h2>
                </Reveal>
              </div>
            </div>

            {/* Narrative — three editorial movements, each with a framed photo */}
            <div className="grid grid-cols-12 gap-10 md:gap-16 mb-32 items-start">
              {[
                {
                  label: "I.",
                  caption: "Observation",
                  src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/l0kx48p5_HotelduCapEdenRocbyRomainRegladepsd_copie.webp",
                  alt: "Observation — the quiet standard of luxury",
                  body:
                    "There is a lot of excellence in luxury. Beautiful products. Refined service. Trained teams. Strong standards.",
                },
                {
                  label: "II.",
                  caption: "Distinction",
                  src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/vmlh91pc_Screenshot%202026-07-20%20at%205.20.16%E2%80%AFpm.png",
                  alt: "Distinction — the moment mastery becomes transformation",
                  body:
                    "But exceptional is different. Exceptional luxury is not the next level of performance. It is the next level of transformation.",
                },
                {
                  label: "III.",
                  caption: "Alignment",
                  src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/jt9ogb4s_Saint%20James%20Paris%20-%20Bar%20Bibliothe%CC%80que%201.webp",
                  alt: "Alignment — soul and system in composition",
                  body:
                    "Exceptional is the rare alignment of soul and system. Soul is the emotional truth of a brand — what clients feel and communities remember. System is the disciplined execution that renders that truth consistent, scalable and unforgettable.",
                },
              ].map((m, i) => (
                <Reveal
                  key={m.label}
                  delay={i * 120}
                  className="col-span-12 md:col-span-4"
                >
                  <div
                    className="p-2 md:p-3"
                    style={{ border: "1px solid #B8B2A6" }}
                  >
                    <ParallaxCard
                      src={m.src}
                      alt={m.alt}
                      aspect="aspect-[3/4]"
                      strength={0.12 + i * 0.02}
                      data-testid={`shift-image-${i + 1}`}
                    />
                  </div>
                  <h3
                    className="title-caps mt-6 leading-tight"
                    style={{
                      fontSize: "clamp(1.15rem, 1.5vw, 1.5rem)",
                      color: "#14140F",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {m.caption}
                  </h3>
                  <div className="mt-3">
                  </div>
                  <p
                    className="font-body mt-3 opacity-75 leading-relaxed"
                    style={{ fontSize: "0.95rem" }}
                  >
                    {m.body}
                  </p>
                </Reveal>
              ))}
            </div>

            {/* Comparative typographic table — full-width, hairlines only */}
            <Reveal delay={120}>
              <ShiftTable />
            </Reveal>
          </div>
        </section>
      )}

      {/* Cinematic Interlude — full-bleed editorial break (training only) */}
      {service === "training" && data.interlude1 && (
        <section
          className="relative w-full overflow-hidden bg-[#0A0A08]"
          style={{ height: "78vh" }}
          data-testid="training-interlude-1"
        >
          <img
            src={data.interlude1.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: "grayscale(1) contrast(1.25) brightness(0.72) saturate(0)",
              animation: "slowKenBurns 26s ease-in-out infinite alternate",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(20,20,15,0.35) 0%, rgba(20,20,15,0.15) 40%, rgba(20,20,15,0.55) 100%)",
            }}
          />
          <div className="relative z-10 h-full flex items-end">
            <div className="max-w-[1440px] mx-auto w-full px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
              <Reveal>
                <p
                  className="font-eyebrow"
                  style={{
                    color: "#C4A76B",
                    letterSpacing: "0.32em",
                    fontSize: "0.72rem",
                  }}
                >
                  Interlude · N°I
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "48px", background: "#C4A76B" }}
                />
                <p
                  className="font-serif-italic mt-8 leading-[1.25]"
                  style={{
                    fontSize: "clamp(1.75rem, 3.2vw, 3rem)",
                    color: "#F4EFE7",
                    maxWidth: "26ch",
                    textWrap: "balance",
                    fontVariationSettings:
                      '"SOFT" 60, "WONK" 1, "opsz" 96',
                    textShadow: "0 1px 24px rgba(0,0,0,0.5)",
                  }}
                >
                  &ldquo;{data.interlude1.caption}&rdquo;
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* CONSULTING — phases / TRAINING — tracks */}
      {/* CONSULTING — phases / TRAINING — programmes (identical sticky-stack layout) */}
      {service === "training" && (
        <section
          className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48"
          style={{ background: "#F4EFE7" }}
          data-testid="training-programmes-header"
        >
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <Reveal>
                <div className="hairline mb-6" />
                <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                  The Programmes
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8">
              <Reveal delay={200}>
                <h2
                  className="title-caps leading-[0.95]"
                  style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
                >
                  TRANSMITTING
                  <br />
                  <span
                    className="font-script"
                    style={{
                      textTransform: "none",
                      letterSpacing: 0,
                      fontSize: "1.25em",
                      color: "#8C6B3F",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Savoir-faire.
                  </span>
                </h2>
              </Reveal>
            </div>
          </div>
        </section>
      )}
      <ConsultingApproach
        phases={service === "training" ? data.tracks : data.phases}
        showHeader={service !== "training"}
      />

      {/* Cinematic Interlude 2 — before Academy (training only) */}
      {service === "training" && data.interlude2 && (
        <section
          className="relative w-full overflow-hidden bg-[#0A0A08] mt-8"
          style={{ height: "70vh" }}
          data-testid="training-interlude-2"
        >
          <img
            src={data.interlude2.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: "grayscale(1) contrast(1.2) brightness(0.75)",
              animation: "slowKenBurns 24s ease-in-out infinite alternate",
              objectPosition: "center 40%",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(0deg, rgba(20,20,15,0.5) 0%, rgba(20,20,15,0.2) 50%, rgba(20,20,15,0.35) 100%)",
            }}
          />
          <div className="relative z-10 h-full flex items-center justify-center">
            <div className="max-w-3xl px-6 text-center">
              <Reveal>
                <p
                  className="font-eyebrow"
                  style={{
                    color: "#C4A76B",
                    letterSpacing: "0.32em",
                    fontSize: "0.72rem",
                  }}
                >
                  Interlude · N°II
                </p>
                <div
                  className="hairline mt-4 mx-auto"
                  style={{ width: "48px", background: "#C4A76B" }}
                />
                <p
                  className="font-serif-italic mt-8 leading-[1.25]"
                  style={{
                    fontSize: "clamp(1.65rem, 3vw, 2.75rem)",
                    color: "#F4EFE7",
                    textWrap: "balance",
                    fontVariationSettings:
                      '"SOFT" 60, "WONK" 1, "opsz" 96',
                    textShadow: "0 1px 24px rgba(0,0,0,0.5)",
                  }}
                >
                  &ldquo;{data.interlude2.caption}&rdquo;
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* CONSULTING — Areas of Expertise (homepage-matched row layout) */}
      {data.expertise && (
        <>
          <section
            className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32"
            data-testid="service-expertise"
          >
            <div className="flex justify-between items-baseline mb-16">
              <div>
                <div className="hairline mb-6" style={{ width: "80px" }} />
                <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                  Areas of Expertise
                </p>
              </div>
              <p className="font-eyebrow opacity-50">Five Territories</p>
            </div>

            <div>
              {data.expertise.map((e, i) => {
                const isHovered = expertiseHover === i;
                const goldOrInk = isHovered ? "#8C6B3F" : "#14140F";
                return (
                  <div
                    key={i}
                    onMouseEnter={() => setExpertiseHover(i)}
                    onMouseLeave={() => setExpertiseHover(null)}
                    className="cursor-default"
                  >
                    <Reveal delay={i * 90} className="grid grid-cols-12 gap-4 py-10">
                      <div className="col-span-12 hairline mb-2" />
                      <div className="col-span-2 md:col-span-1">
                        <p
                          className="title-caps"
                          style={{
                            fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                            color: goldOrInk,
                            opacity: 0.55,
                            letterSpacing: "0.06em",
                            transition: "color 600ms ease-out",
                          }}
                        >
                          {toRoman(i)}
                        </p>
                      </div>
                      <div className="col-span-10 md:col-span-5">
                        <h3
                          className="title-caps leading-[0.95]"
                          style={{
                            fontSize: "clamp(1.6rem, 3.2vw, 2.5rem)",
                            color: goldOrInk,
                            transition: "color 600ms ease-out",
                          }}
                        >
                          {e.caps}
                          {e.script && (
                            <>
                              {" "}
                              <span
                                className="font-script"
                                style={{
                                  textTransform: "none",
                                  letterSpacing: 0,
                                  fontSize: "1.4em",
                                  color: "#8C6B3F",
                                }}
                              >
                                {e.script}
                              </span>
                            </>
                          )}
                        </h3>
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <ul className="space-y-1.5">
                          {e.items.map((it, j) => (
                            <li
                              key={j}
                              className="font-body opacity-75 leading-relaxed"
                              style={{
                                color: goldOrInk,
                                transition: "color 600ms ease-out",
                              }}
                            >
                              — {it}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Reveal>
                  </div>
                );
              })}
              <div className="hairline mt-2" />
            </div>
          </section>
        </>
      )}


      {/* CONSULTING — Roadmap (scroll-triggered timeline with progress line) */}
      {data.roadmap && <ConsultingRoadmap roadmap={data.roadmap} />}

      {/* CONSULTING — Industries cinematic full-width (matches Home) */}
      {service === "consulting" && (
        <section
          className="relative w-full overflow-hidden bg-[#0A0A08]"
          style={{ height: "88vh" }}
          data-testid="consulting-industries-cinematic"
        >
          {[
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/l0kx48p5_HotelduCapEdenRocbyRomainRegladepsd_copie.webp",
              title: "Luxury Hospitality",
            },
            {
              src: "/aquarama-clean.jpg",
              title: "Yachting & Automotive",
            },
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/w5sue1sv_Ana-de-Armas-louis-vuitton-deep-time-robb-report-italia.jpg",
              title: "Luxury Retail",
            },
          ].map((frame, i) => (
            <div
              key={frame.title}
              className="absolute inset-0"
              style={{
                animation: `consultingFade 12s ease-in-out ${i * 4}s infinite`,
                opacity: 0,
              }}
            >
              <img
                src={frame.src}
                alt={frame.title}
                className="w-full h-full object-cover"
                style={{
                  filter: "contrast(1.06) saturate(1.05) brightness(0.94)",
                  animation: `consultingKen 12s ease-in-out ${i * 4}s infinite`,
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0.65) 100%)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <h2
                  className="title-caps text-center"
                  style={{
                    color: "#F4EFE7",
                    fontSize: "clamp(2rem, 5.5vw, 5.5rem)",
                    letterSpacing: "0.04em",
                    lineHeight: 1.05,
                    textWrap: "balance",
                    textShadow: "0 2px 24px rgba(0,0,0,0.5)",
                  }}
                >
                  {frame.title}
                </h2>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* TRAINING — Academy Programmes */}
      {data.academy && (
        <>
          <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-12 gap-8" data-testid="service-academy">
            <div className="col-span-12 md:col-span-4">
              <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>Public Programmes</p>
              <h2 className="title-caps mt-8 leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
                {data.academy.caps}
                <br />
                <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.35em", color: "#8C6B3F" }}>{data.academy.script}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <Reveal>
                <p className="font-body text-lg opacity-85 leading-relaxed max-w-2xl">{data.academy.dek}</p>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {data.academy.formats.map((f, i) => (
                    <div key={i}>
                      <div className="hairline mb-6" />
                      <p className="font-eyebrow opacity-60">Format N°{String(i + 1).padStart(2, "0")}</p>
                      <h4 className="title-caps mt-3" style={{ fontSize: "clamp(1.35rem, 2vw, 1.75rem)" }}>
                        {f.caps}{" "}
                        <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.5em", color: "#8C6B3F" }}>{f.script}</span>
                      </h4>
                      <p className="font-body mt-4 opacity-75">{f.dek}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </section>
        </>
      )}

      {/* TRAINING — Industries cinematic full-width (matches Home & Consulting) */}
      {service === "training" && (
        <section
          className="relative w-full overflow-hidden bg-[#0A0A08]"
          style={{ height: "88vh" }}
          data-testid="training-industries-cinematic"
        >
          {[
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/l0kx48p5_HotelduCapEdenRocbyRomainRegladepsd_copie.webp",
              title: "Luxury Hospitality",
            },
            {
              src: "/aquarama-clean.jpg",
              title: "Yachting & Automotive",
            },
            {
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/w5sue1sv_Ana-de-Armas-louis-vuitton-deep-time-robb-report-italia.jpg",
              title: "Luxury Retail",
            },
          ].map((frame, i) => (
            <div
              key={frame.title}
              className="absolute inset-0"
              style={{
                animation: `consultingFade 12s ease-in-out ${i * 4}s infinite`,
                opacity: 0,
              }}
            >
              <img
                src={frame.src}
                alt={frame.title}
                className="w-full h-full object-cover"
                style={{
                  filter: "contrast(1.06) saturate(1.05) brightness(0.94)",
                  animation: `consultingKen 12s ease-in-out ${i * 4}s infinite`,
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0.65) 100%)",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center px-6">
                <h2
                  className="title-caps text-center"
                  style={{
                    color: "#F4EFE7",
                    fontSize: "clamp(2rem, 5.5vw, 5.5rem)",
                    letterSpacing: "0.04em",
                    lineHeight: 1.05,
                    textWrap: "balance",
                    textShadow: "0 2px 24px rgba(0,0,0,0.5)",
                  }}
                >
                  {frame.title}
                </h2>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* CTA — training gets a dark cinematic panel */}
      {service === "training" ? (
        <section
          className="relative w-full overflow-hidden"
          style={{ background: "#14140F", color: "#F4EFE7" }}
          data-testid="service-cta"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 md:col-span-2">
                <p
                  className="font-eyebrow"
                  style={{
                    color: "#C4A76B",
                    letterSpacing: "0.32em",
                    fontSize: "0.72rem",
                  }}
                >
                  By Appointment
                </p>
                <div
                  className="hairline mt-4"
                  style={{ width: "24px", background: "#C4A76B" }}
                />
              </div>
              <div className="col-span-12 md:col-span-9">
                <Reveal>
                  <h2
                    className="title-caps leading-[1.0]"
                    style={{
                      fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                      letterSpacing: "-0.015em",
                      color: "#F4EFE7",
                      textWrap: "balance",
                    }}
                  >
                    Every exceptional brand begins with{" "}
                    <span
                      className="font-script"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: "1.2em",
                        color: "#C4A76B",
                      }}
                    >
                      exceptional people.
                    </span>
                  </h2>
                  <div
                    className="hairline mt-10 mb-8"
                    style={{ width: "48px", background: "#C4A76B" }}
                  />
                  <p
                    className="font-serif-italic"
                    style={{
                      fontSize: "clamp(1.1rem, 1.35vw, 1.4rem)",
                      lineHeight: 1.55,
                      color: "#F4EFE7",
                      opacity: 0.82,
                      maxWidth: "56ch",
                      textWrap: "balance",
                      fontVariationSettings:
                        '"SOFT" 60, "WONK" 1, "opsz" 72',
                    }}
                  >
                    Let’s design the learning journey your organisation deserves.
                  </p>
                  <div className="mt-12">
                    <Link
                      to="/private-inquiry"
                      data-testid="service-inquiry-link"
                      className="inline-flex items-center gap-4"
                      style={{
                        padding: "1.1rem 2.2rem",
                        border: "1px solid #F4EFE7",
                        background: "#F4EFE7",
                        color: "#14140F",
                        letterSpacing: "0.28em",
                        fontSize: "0.72rem",
                        fontFamily: "'Marcellus SC', serif",
                        textTransform: "uppercase",
                        transition:
                          "background-color 400ms ease, color 400ms ease, letter-spacing 400ms ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#F4EFE7";
                        e.currentTarget.style.letterSpacing = "0.34em";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#F4EFE7";
                        e.currentTarget.style.color = "#14140F";
                        e.currentTarget.style.letterSpacing = "0.28em";
                      }}
                    >
                      Begin a Conversation
                      <span aria-hidden="true">⟶</span>
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-12 gap-6" data-testid="service-cta">
          <div className="col-span-12 md:col-span-8 md:col-start-3 text-center">
            <p className="font-eyebrow opacity-60">By Appointment</p>
            <p className="font-serif-italic mt-8 leading-[1.1]" style={{ fontSize: "clamp(1.5rem, 2.8vw, 2.5rem)" }}>
              Every engagement begins with a private conversation.
            </p>
            <Link to="/private-inquiry" className="btn-ghost mt-12 inline-flex" data-testid="service-inquiry-link">
              Request a Consultation <span className="arrow">→</span>
            </Link>
          </div>
        </section>
      )}

      {/* Continue */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-24 flex justify-between items-end">
        <div>
          <p className="font-eyebrow opacity-60">The Other Discipline</p>
          <h2 className="title-caps mt-4" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
            {data.next.label}{" "}
            <span className="font-script" style={{ textTransform: "none", letterSpacing: 0, fontSize: "1.4em", color: "#8C6B3F" }}>
              {data.next.script}
            </span>
          </h2>
        </div>
        <Link to={data.next.to} className="btn-ghost" data-testid="service-next-link">
          Continue <span className="arrow">→</span>
        </Link>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ConsultingApproach — sticky-stacking phases with scroll parallax
// Each phase pinned at top: 0 for 100vh of scroll; next phase slides
// over from below with a subtle translateY parallax on the background.
// ─────────────────────────────────────────────────────────────
function ConsultingApproach({ phases, showHeader = true }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = el.getBoundingClientRect();
        const total = el.offsetHeight - window.innerHeight;
        if (total <= 0) return;
        const scrolled = Math.max(0, -rect.top);
        const global = Math.min(1, scrolled / total);
        el.style.setProperty("--approach-progress", global);
        phases.forEach((_, i) => {
          const start = i / phases.length;
          const end = (i + 1) / phases.length;
          const local = Math.max(0, Math.min(1, (global - start) / (end - start)));
          el.style.setProperty(`--phase-${i}-progress`, local);
        });
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [phases]);

  return (
    <>
      {showHeader && (
      <section
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48"
        style={{ background: "#F4EFE7" }}
        data-testid="service-blocks-header"
      >
        <div className="grid grid-cols-12 gap-8">
          {/* Left — matched to Founders' Note formatting */}
          <div className="col-span-12 md:col-span-4">
            <Reveal>
              <div className="hairline mb-6" />
              <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
                Our Approach
              </p>
            </Reveal>
          </div>

          {/* Right — big caps + script title, body, cta link */}
          <div className="col-span-12 md:col-span-8">
            <Reveal delay={200}>
              <h2
                className="title-caps leading-[0.95]"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 5rem)",
                }}
              >
                FROM EXCELLENT
                <br />
                TO{" "}
                <span
                  className="font-script"
                  style={{
                    textTransform: "none",
                    letterSpacing: 0,
                    fontSize: "1.25em",
                    color: "#8C6B3F",
                  }}
                >
                  Exceptional.
                </span>
              </h2>

              <p className="font-body mt-12 max-w-2xl opacity-85 text-lg leading-relaxed">
                We craft, refine, and elevate luxury experiences through a
                holistic approach that bridges strategy, culture, experience
                design, operational mastery, and implementation &mdash;
                transforming excellence into something lived, human, and
                enduring. Because exceptional luxury is not built through
                concepts alone. It is built when standards become instinct,
                service feels intentional, and experiences are consistently
                embodied across every touchpoint.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Vertical scroll cue — centred under section */}
        <div className="flex justify-center mt-20 md:mt-24">
          <a
            href="#service-blocks"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("[data-testid=service-blocks]");
              if (el)
                window.scrollTo({
                  top: el.getBoundingClientRect().top + window.scrollY - 40,
                  behavior: "smooth",
                });
            }}
            data-testid="approach-scroll-cue"
            aria-label="Scroll to phases"
            className="scroll-cue-dark"
          />
        </div>
      </section>
      )}

      <div
        ref={containerRef}
        className="relative"
        data-testid="service-blocks"
        style={{ background: "#0A0A08" }}
      >
        {phases.map((b, i) => {
          const isSpread = b.layout === "spread";
          return (
          <section
            key={i}
            className="sticky top-0 w-full overflow-hidden"
            style={{
              height: "100vh",
              zIndex: 10 + i,
              background: isSpread ? "#0F0F0D" : "#0A0A08",
            }}
            data-testid={`consulting-phase-${i}`}
          >
            {isSpread ? (
              <SpreadPhase phase={b} index={i} />
            ) : (
              <>
            {/* Cinematic image — natural colour, parallax + scale */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${b.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "contrast(1.05) brightness(0.95) saturate(1)",
                transform: `translate3d(0, calc(var(--phase-${i}-progress, 0) * -14%), 0) scale(calc(1.15 + var(--phase-${i}-progress, 0) * 0.06))`,
                transformOrigin: "center center",
                willChange: "transform",
                transition: "transform 80ms linear",
              }}
            />
            {/* Gradient overlay — softened for colour photography */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(20,20,15,0.30) 0%, rgba(20,20,15,0.12) 40%, rgba(20,20,15,0.18) 65%, rgba(20,20,15,0.45) 100%)",
              }}
            />
            {/* Vignette */}
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(130% 100% at 50% 50%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
                pointerEvents: "none",
              }}
            />

            {/* Foreground content — subtle counter-parallax up/down */}
            <div
              className="relative z-10 h-full flex items-center"
              style={{
                transform: `translate3d(0, calc(var(--phase-${i}-progress, 0) * 4%), 0)`,
                willChange: "transform",
              }}
            >
              <div className="max-w-[1440px] mx-auto w-full px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-10 items-center">
                <div className="col-span-12 md:col-span-6">
                  <p
                    className="font-eyebrow"
                    style={{
                      color: "#C4A76B",
                      letterSpacing: "0.32em",
                      fontSize: "0.72rem",
                    }}
                  >
                    {b.eyebrow}
                  </p>
                  <div
                    className="hairline mt-4"
                    style={{ width: "48px", background: "#C4A76B" }}
                  />
                  <h3
                    className="leading-[0.95] mt-10"
                    style={{ fontSize: "clamp(2.25rem, 4.6vw, 4.5rem)" }}
                  >
                    <span
                      className="title-caps"
                      style={{
                        display: "block",
                        color: "#F4EFE7",
                        letterSpacing: "-0.015em",
                        textShadow: "0 1px 24px rgba(0,0,0,0.4)",
                      }}
                    >
                      {b.caps}
                    </span>
                    <span
                      className="font-script"
                      style={{
                        display: "block",
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: "1.15em",
                        color: "#C4A76B",
                        marginTop: "0.05em",
                        textShadow: "0 1px 24px rgba(0,0,0,0.4)",
                      }}
                    >
                      {b.script}
                    </span>
                  </h3>
                </div>

                <div className="col-span-12 md:col-span-6 md:pl-8">
                  <p className="font-body opacity-85 text-lg leading-relaxed" style={{ color: "#F4EFE7", maxWidth: "50ch", textShadow: "0 1px 16px rgba(0,0,0,0.35)" }}>
                    {b.dek}
                  </p>
                  <ul className="mt-10 space-y-0">
                    {b.items.slice(0, 6).map((it, j) => (
                      <li
                        key={j}
                        className="flex items-baseline gap-6 py-3"
                        style={{
                          borderTop:
                            j === 0
                              ? "1px solid rgba(244,239,231,0.30)"
                              : "1px solid rgba(244,239,231,0.12)",
                        }}
                      >
                        <span
                          className="font-eyebrow"
                          style={{
                            color: "#C4A76B",
                            letterSpacing: "0.28em",
                            fontSize: "0.62rem",
                            minWidth: "1.8rem",
                          }}
                        >
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <span className="font-body opacity-90" style={{ color: "#F4EFE7", fontSize: "0.95rem", lineHeight: 1.55 }}>
                          {it}
                        </span>
                      </li>
                    ))}
                    <li
                      style={{
                        borderTop: "1px solid rgba(244,239,231,0.12)",
                      }}
                    />
                  </ul>
                </div>
              </div>
            </div>

            {/* Chapter indicator */}
            <div
              className="absolute bottom-8 right-8 md:right-16 z-10"
              style={{ color: "#C4A76B" }}
            >
              <p
                className="font-eyebrow"
                style={{
                  letterSpacing: "0.32em",
                  fontSize: "0.68rem",
                  opacity: 0.85,
                }}
              >
                Chapter {b.n} · of {phases.length}
              </p>
            </div>
              </>
            )}
          </section>
        );})}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// SpreadPhase — magazine double-page layout.
// Photo on the left (60% width) with a gradient fade on the right
// edge that dissolves into the Ivory page background. Text sits on
// the right half in ink on ivory, editorially calm and readable.
// ─────────────────────────────────────────────────────────────
function SpreadPhase({ phase: b, index: i }) {
  return (
    <>
      {/* Photo — left 52% with parallax; focus on lower portion (bracelet + reflection) */}
      <div
        className="absolute inset-y-0 left-0"
        style={{
          width: "52%",
          overflow: "hidden",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${b.image})`,
            backgroundSize: "cover",
            backgroundPosition: b.focus || "center bottom",
            filter: "contrast(1.06) brightness(0.94) saturate(1.02)",
            transform: `translate3d(0, calc(var(--phase-${i}-progress, 0) * -6%), 0) scale(calc(1.02 + var(--phase-${i}-progress, 0) * 0.03))`,
            transformOrigin: "center bottom",
            willChange: "transform",
            transition: "transform 80ms linear",
          }}
        />
        {/* Right-edge gradient fade dissolving into deep ink */}
        <div
          aria-hidden
          className="absolute inset-y-0 right-0"
          style={{
            width: "50%",
            background:
              "linear-gradient(to right, rgba(15,15,13,0) 0%, rgba(15,15,13,0.55) 45%, rgba(15,15,13,0.92) 78%, #0F0F0D 100%)",
            pointerEvents: "none",
          }}
        />
        {/* Subtle vignette on bottom-left of photo */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 100% at 30% 60%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.35) 100%)",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Text — right column on deep ink */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-[1440px] mx-auto w-full px-6 md:px-12 lg:px-20 grid grid-cols-12 gap-8">
          <div className="col-start-1 md:col-start-7 col-span-12 md:col-span-6">
            <p
              className="font-eyebrow"
              style={{
                color: "#8C6B3F",
                letterSpacing: "0.32em",
                fontSize: "0.72rem",
              }}
            >
              {b.eyebrow}
            </p>
            <div
              className="hairline mt-4"
              style={{ width: "48px", background: "#8C6B3F" }}
            />
            <h3
              className="leading-[0.95] mt-10"
              style={{ fontSize: "clamp(2.25rem, 4.6vw, 4.5rem)" }}
            >
              <span
                className="title-caps"
                style={{
                  display: "block",
                  color: "#F4EFE7",
                  letterSpacing: "-0.015em",
                  textShadow: "0 1px 24px rgba(0,0,0,0.4)",
                }}
              >
                {b.caps}
              </span>
              <span
                className="font-script"
                style={{
                  display: "block",
                  textTransform: "none",
                  letterSpacing: 0,
                  fontSize: "1.15em",
                  color: "#8C6B3F",
                  marginTop: "0.05em",
                  textShadow: "0 1px 24px rgba(0,0,0,0.4)",
                }}
              >
                {b.script}
              </span>
            </h3>
            <p className="font-body opacity-85 text-lg leading-relaxed mt-10" style={{ color: "#F4EFE7", maxWidth: "50ch", textShadow: "0 1px 16px rgba(0,0,0,0.35)" }}>
              {b.dek}
            </p>
            <ul className="mt-8 space-y-0">
              {b.items.slice(0, 6).map((it, j) => (
                <li
                  key={j}
                  className="flex items-baseline gap-6 py-3"
                  style={{
                    borderTop:
                      j === 0
                        ? "1px solid rgba(244,239,231,0.30)"
                        : "1px solid rgba(244,239,231,0.12)",
                  }}
                >
                  <span
                    className="font-eyebrow"
                    style={{
                      color: "#8C6B3F",
                      letterSpacing: "0.28em",
                      fontSize: "0.62rem",
                      minWidth: "1.8rem",
                    }}
                  >
                    {String(j + 1).padStart(2, "0")}
                  </span>
                  <span className="font-body opacity-90" style={{ color: "#F4EFE7", fontSize: "0.95rem", lineHeight: 1.55 }}>
                    {it}
                  </span>
                </li>
              ))}
              <li
                style={{ borderTop: "1px solid rgba(244,239,231,0.12)" }}
              />
            </ul>
          </div>
        </div>
      </div>

      {/* Chapter indicator */}
      <div
        className="absolute bottom-8 right-8 md:right-16 z-10"
        style={{ color: "#8C6B3F" }}
      >
        <p
          className="font-eyebrow"
          style={{
            letterSpacing: "0.32em",
            fontSize: "0.68rem",
            opacity: 0.85,
          }}
        >
          Chapter {b.n} · of 3
        </p>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ConsultingRoadmap — horizontal scroll-triggered timeline.
// A gold progress line draws itself left→right as the reader
// scrolls through the section. Each stage's dot fills at its
// position on the line and the copy fades in from below.
// ─────────────────────────────────────────────────────────────
function ConsultingRoadmap({ roadmap }) {
  const [hover, setHover] = useState(null);

  const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

  // Timeline progress driven by hover position (0 → 1)
  const progress =
    hover === null ? 0 : (hover + 0.5) / roadmap.length;

  return (
    <section
      className="relative w-full"
      style={{ background: "#F4EFE7" }}
      data-testid="service-roadmap"
    >
      {/* Header — matched to Founders' Note formatting */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-48 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <div className="hairline mb-6" />
          <p className="font-eyebrow" style={{ color: "#8C6B3F" }}>
            The Road Map
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <h2
            className="title-caps leading-[0.95]"
            style={{ fontSize: "clamp(2.25rem, 5vw, 5rem)" }}
          >
            A CURATED{" "}
            <span
              className="font-script"
              style={{
                textTransform: "none",
                letterSpacing: 0,
                fontSize: "1.25em",
                color: "#8C6B3F",
              }}
            >
              journey.
            </span>
          </h2>
          <p className="font-body mt-12 max-w-2xl opacity-85 text-lg leading-relaxed">
            Five stages, tailored to the House. Each engagement unfolds at
            its own rhythm — never rushed, never rehearsed. Hover a stage
            to reveal its rhythm along the line.
          </p>
        </div>
      </div>

      {/* Horizontal timeline — hover activated */}
      <div
        className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-32 md:pb-40"
        data-testid="roadmap-timeline"
        onMouseLeave={() => setHover(null)}
      >
        <div className="relative">
          {/* Base hairline */}
          <div
            aria-hidden
            className="absolute left-0 right-0"
            style={{
              top: "6px",
              height: "1px",
              background: "rgba(20,20,15,0.15)",
            }}
          />
          {/* Progress hairline — grows to the hovered stage */}
          <div
            aria-hidden
            className="absolute left-0"
            style={{
              top: "6px",
              height: "1px",
              background: "#8C6B3F",
              width: `${progress * 100}%`,
              transition:
                "width 700ms cubic-bezier(0.22, 0.61, 0.36, 1)",
              willChange: "width",
              boxShadow: "0 0 12px rgba(140,107,63,0.4)",
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-6">
            {roadmap.map((r, i) => {
              const stagePos = (i + 0.5) / roadmap.length;
              const reached = hover !== null && progress >= stagePos - 0.02;
              const isActive = hover === i;
              return (
                <div
                  key={i}
                  data-testid={`roadmap-stage-${i}`}
                  className="relative flex flex-col items-start cursor-pointer"
                  onMouseEnter={() => setHover(i)}
                  style={{
                    opacity: isActive ? 1 : 0.5,
                    transform: isActive
                      ? "translateY(-4px)"
                      : "translateY(0)",
                    transition:
                      "opacity 600ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                  }}
                >
                  {/* Dot on the hairline */}
                  <span
                    aria-hidden
                    style={{
                      position: "relative",
                      width: "13px",
                      height: "13px",
                      borderRadius: "50%",
                      border: "1px solid #8C6B3F",
                      background: reached ? "#8C6B3F" : "#F4EFE7",
                      boxShadow: isActive
                        ? "0 0 0 8px rgba(140,107,63,0.12), 0 0 32px rgba(140,107,63,0.5)"
                        : reached
                        ? "0 0 0 4px rgba(140,107,63,0.08), 0 0 16px rgba(140,107,63,0.25)"
                        : "none",
                      transition:
                        "background-color 600ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 600ms ease, transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                      transform: isActive ? "scale(1.2)" : "scale(1)",
                      zIndex: 2,
                    }}
                  />

                  {/* Roman numeral — matched in size to Areas of Expertise */}
                  <p
                    className="title-caps mt-10"
                    style={{
                      fontSize: "clamp(1.4rem, 2.2vw, 1.9rem)",
                      color: isActive ? "#8C6B3F" : "#14140F",
                      opacity: 0.55,
                      letterSpacing: "0.06em",
                      transition:
                        "color 600ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                    }}
                  >
                    {ROMAN[i]}
                  </p>

                  {/* Title (caps) — sized to fit 5 columns without overflow */}
                  <h3
                    className="title-caps mt-5 leading-[0.98]"
                    style={{
                      fontSize: "clamp(1.3rem, 2vw, 1.85rem)",
                      letterSpacing: "-0.005em",
                      color: "#14140F",
                      transition: "letter-spacing 600ms ease",
                      overflowWrap: "break-word",
                    }}
                  >
                    {r.caps}
                  </h3>

                  {/* Script accent — its own line, animates in on hover */}
                  {r.script && (
                    <p
                      className="font-script"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        fontSize: "clamp(1.5rem, 2.4vw, 2.1rem)",
                        color: "#8C6B3F",
                        lineHeight: 1,
                        marginTop: "0.4rem",
                        opacity: isActive ? 1 : 0.5,
                        transform: isActive
                          ? "translateY(0)"
                          : "translateY(6px)",
                        transition:
                          "opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1)",
                      }}
                    >
                      {r.script}
                    </p>
                  )}

                  {/* Body dek */}
                  <p
                    className="font-body mt-6 opacity-75 leading-relaxed"
                    style={{
                      fontSize: "0.9rem",
                      opacity: isActive ? 0.85 : 0.55,
                      transition: "opacity 600ms ease",
                    }}
                  >
                    {r.dek}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// ShiftTable — sophisticated comparative table Excellent | Exceptional
// ─────────────────────────────────────────────────────────────
function ShiftTable() {
  const rows = [
    { left: "Beautiful products", right: "Purposeful brand" },
    { left: "Refined service", right: "Human connection" },
    { left: "Trained teams", right: "Embodied culture" },
    { left: "Rigid brand standards", right: "Intuitive mastery" },
    { left: "Performance", right: "Transformation" },
  ];

  return (
    <div
      className="relative"
      data-testid="shift-comparison-table"
    >
      {/* Column headers — pure editorial, no borders */}
      <div
        className="grid grid-cols-12 items-baseline pb-10"
        style={{ borderBottom: "1px solid rgba(20,20,15,0.20)" }}
      >
        <div className="col-span-6 md:col-span-5 pr-4">
          <p
            className="font-eyebrow"
            style={{
              color: "rgba(20,20,15,0.45)",
              letterSpacing: "0.42em",
              fontSize: "0.6rem",
            }}
          >
            The Common Standard
          </p>
          <p
            className="font-script mt-4"
            style={{
              fontSize: "clamp(2.5rem, 4.4vw, 4.25rem)",
              color: "rgba(20,20,15,0.5)",
              textTransform: "none",
              letterSpacing: 0,
              lineHeight: 0.95,
            }}
          >
            Excellent
          </p>
        </div>
        <div className="col-span-1 md:col-span-2 flex justify-center items-baseline">
          {/* versus label removed per editorial refinement */}
        </div>
        <div className="col-span-5 md:col-span-5 pl-4">
          <p
            className="font-eyebrow"
            style={{
              color: "#8C6B3F",
              letterSpacing: "0.42em",
              fontSize: "0.6rem",
            }}
          >
            The House Standard
          </p>
          <p
            className="title-caps mt-4"
            style={{
              fontSize: "clamp(2.25rem, 4vw, 3.75rem)",
              color: "#8C6B3F",
              letterSpacing: "-0.01em",
              lineHeight: 0.95,
            }}
          >
            EXCEPTIONAL
          </p>
        </div>
      </div>

      {/* Central vertical hairline spine (desktop only) */}
      <div
        aria-hidden
        className="hidden md:block absolute pointer-events-none"
        style={{
          left: "calc(50% - 0.5px)",
          top: "clamp(6rem, 8vw, 8rem)",
          bottom: 0,
          width: "1px",
          background: "rgba(20,20,15,0.10)",
        }}
      />

      {/* Rows — pure typographic, hairline only between rows */}
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-12 items-baseline py-8 md:py-10 group"
          style={{
            borderBottom:
              i < rows.length - 1
                ? "1px solid rgba(20,20,15,0.10)"
                : "none",
          }}
        >
          <div className="col-span-6 md:col-span-5 pr-4 md:pr-10 flex items-baseline gap-5">
            <span
              className="font-eyebrow"
              style={{
                color: "rgba(20,20,15,0.28)",
                letterSpacing: "0.32em",
                fontSize: "0.6rem",
                minWidth: "1.6rem",
              }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span
              className="font-serif-display"
              style={{
                fontSize: "clamp(1.05rem, 1.15vw, 1.25rem)",
                lineHeight: 1.5,
                color: "rgba(20,20,15,0.5)",
                letterSpacing: "-0.005em",
                fontFeatureSettings: '"liga" 1, "kern" 1, "onum" 1',
              }}
            >
              {r.left}
            </span>
          </div>
          <div className="col-span-1 md:col-span-2 flex justify-center items-baseline">
            <span
              aria-hidden
              style={{
                color: "#8C6B3F",
                fontSize: "0.72rem",
                letterSpacing: "0.28em",
                opacity: 0.35,
                transition: "opacity 500ms ease, transform 500ms ease",
                display: "inline-block",
              }}
              className="font-eyebrow group-hover:opacity-100 group-hover:translate-x-2"
            >
              ⟶
            </span>
          </div>
          <div className="col-span-5 md:col-span-5 pl-4 md:pl-10">
            <span
              className="title-caps"
              style={{
                fontSize: "clamp(1.05rem, 1.25vw, 1.4rem)",
                lineHeight: 1.35,
                color: "#14140F",
                letterSpacing: "0.04em",
              }}
            >
              {r.right}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

